// Layout1.jsx
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Box,
  Typography,
  Alert,
} from "@mui/material";
import TableRowsIcon from "@mui/icons-material/TableRows";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import usePermissionDataStore from "../../Zustand/Store/usePermissionDataStore";
import { hasPermission } from "../../utils/permissionUtils";
import NotAllowed from "../../Exceptions/NotAllowed";

const Layout1 = ({
  loading,
  heading,
  Route,
  btnName,
  Data,
  tableHeaders,
  setData,
  Icons,
  messages,
  DeleteFunc,
  showActions,
  showHeaders,
  delete_action,
  add_action,
  edit_action,
  search,
  setSearch,
  rowsPerPage = 6,
  deleteConfirmTitle,
  deleteConfirmMessage,
  hideViewToggle = false,
  dense = false,
}) => {
  console.log("table ", tableHeaders);

  const navigate = useNavigate();
  const [view, setView] = useState("table");
  const [deleteDialog, setDeleteDialog] = useState(null);
  const { Permission, isPermissionLoaded } = usePermissionDataStore();
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(rowsPerPage);
  const [showNotAllowed, setShowNotAllowed] = useState(false);
  const [localSearch, setLocalSearch] = useState("");

  const searchValue = search !== undefined ? search : localSearch;
  const handleSearchChange = (value) => {
    if (typeof setSearch === "function") {
      setSearch(value);
    } else {
      setLocalSearch(value);
    }
    setPage(0);
  };

  const filteredData = useMemo(() => {
    const term = (searchValue || "").trim().toLowerCase();
    if (!term || !Array.isArray(Data)) return Data || [];
    return Data.filter((row) => {
      const name = String(
        row.employee_name || row.person_name || row.name || ""
      ).toLowerCase();
      const code = String(row.employee_code || row.person_code || "").toLowerCase();
      return name.includes(term) || code.includes(term);
    });
  }, [Data, searchValue]);

  const hasAddPermission = useMemo(
    () => hasPermission(Permission, add_action, isPermissionLoaded),
    [add_action, Permission, isPermissionLoaded]
  );

  const hasEditPermission = useMemo(
    () => hasPermission(Permission, edit_action, isPermissionLoaded),
    [edit_action, Permission, isPermissionLoaded]
  );

  const hasDeletePermission = useMemo(
    () => hasPermission(Permission, delete_action, isPermissionLoaded),
    [delete_action, Permission, isPermissionLoaded]
  );

  const offset = page * perPage;
  const current = filteredData?.slice(offset, offset + perPage);
  const totalPages = Math.ceil((filteredData?.length || 0) / perPage) || 1;

  const confirmDelete = async () => {
    if (!hasDeletePermission) {
      setDeleteDialog(null);
      setShowNotAllowed(true);
      return;
    }
    await DeleteFunc(deleteDialog).catch(() => toast.error("Delete failed"));
    setData((prev) => prev.filter((i) => i.id !== deleteDialog));
    toast.success(`${messages[3]} deleted`);
    setDeleteDialog(null);
  };

  const changeView = (_, v) => v && setView(v);
  const changePage = (delta) => {
    const np = page + delta;
    if (np >= 0 && np < totalPages) setPage(np);
  };

  const activeView = hideViewToggle ? "table" : view;

  if (showNotAllowed) {
    return <NotAllowed />;
  }

  return (
    <Box p={{ xs: 2, md: 4 }}>
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <Box
            display="flex"
            flexWrap="wrap"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
            mb={2}
          >
            <Typography
              variant="h5"
              component="div"
              sx={{ fontWeight: 600, flex: 1, minWidth: 0, width: "100%" }}
            >
              {heading}
            </Typography>
            <Box display="flex" gap={1} alignItems="center">
              {!hideViewToggle && (
                <ToggleButtonGroup
                  value={view}
                  exclusive
                  onChange={changeView}
                  size="small"
                >
                  <ToggleButton value="table">
                    <TableRowsIcon />
                  </ToggleButton>
                  <ToggleButton value="card">
                    <ViewModuleIcon />
                  </ToggleButton>
                  <ToggleButton value="list">
                    <ViewListIcon />
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
              {btnName && hasAddPermission && (
                <Button
                  variant="contained"
                  onClick={() => navigate(`${Route}/add`)}
                >
                  {btnName}
                </Button>
              )}
            </Box>
          </Box>
          <TextField
            placeholder="Search by name..."
            fullWidth
            value={searchValue || ""}
            onChange={(e) => handleSearchChange(e.target.value)}
            sx={{ mb: 3 }}
          />

          {!filteredData?.length ? (
            <Box textAlign="center" mt={8}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No results found{searchValue ? ` for “${searchValue}”` : ""}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Try adjusting your search
                {hasAddPermission && (
                  <>
                    &nbsp;or&nbsp;
                    <Button size="small" onClick={() => navigate(`${Route}/add`)}>
                      Add {messages[1]}
                    </Button>
                  </>
                )}
                .
              </Typography>
            </Box>
          ) : activeView === "card" ? (
            <Box
              display="grid"
              gridTemplateColumns="repeat(auto-fit,minmax(250px,1fr))"
              gap={2}
            >
              {current.map((i) => (
                <Box
                  key={i.id}
                  sx={{
                    border: "1px solid #ddd",
                    borderRadius: 2,
                    p: 2,
                    height: 240,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    transition: "transform .2s",
                    "&:hover": {
                      transform: "scale(1.02)",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                    },
                  }}
                >
                  <Box>
                    <Typography variant="h6">{i.name}</Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      {i.description}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1}>
                    {hasEditPermission && (
                      <Button
                        size="small"
                        onClick={() => navigate(`${Route}/edit/${i.id}`)}
                      >
                        Edit
                      </Button>
                    )}
                    {hasDeletePermission && (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => setDeleteDialog(i.id)}
                      >
                        Delete
                      </Button>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          ) : activeView === "table" ? (
            <Paper
              elevation={0}
              sx={{
                overflowX: "auto",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <TableContainer>
                <Table
                  size={dense ? "small" : "medium"}
                  sx={{
                    width: "100%",
                    minWidth: dense ? 640 : 900,
                    tableLayout: dense ? "fixed" : "auto",
                    ...(dense && {
                      "& .MuiTableCell-root": {
                        py: 0.75,
                        px: 1.5,
                        fontSize: "0.8125rem",
                        lineHeight: 1.35,
                      },
                      "& .MuiTableCell-head": {
                        py: 1,
                        fontSize: "0.8125rem",
                      },
                      "& .MuiChip-root": {
                        height: 22,
                        fontSize: "0.75rem",
                      },
                    }),
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {tableHeaders?.length > 0 ? (
                        tableHeaders?.map((item, id) => {
                          return (
                            <TableCell
                              style={
                                item.width == undefined
                                  ? { flex: item.flex }
                                  : { width: item.width }
                              }
                              key={id}
                            >
                              <b>{item.name}</b>
                            </TableCell>
                          );
                        })
                      ) : (
                        <>
                          <TableCell>
                            <b>Name</b>
                          </TableCell>
                          <TableCell>
                            <b>Description</b>
                          </TableCell>
                        </>
                      )}
                      {(hasEditPermission || hasDeletePermission) && (
                        <TableCell align="right">
                          <b>Actions</b>
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {current.map((i) => (
                      <TableRow key={i.id} hover>
                        {tableHeaders != undefined ? (
                          tableHeaders?.map((item, id) => {
                            return (
                              <TableCell
                                style={
                                  item.textStyle == undefined
                                    ? {}
                                    : { textTransform: item.textStyle }
                                }
                              >
                                {i?.[item.value_key]}
                              </TableCell>
                            );
                          })
                        ) : (
                          <>
                            <TableCell>{i.name}</TableCell>
                            <TableCell>{i.description}</TableCell>
                          </>
                        )}
                        {(hasEditPermission || hasDeletePermission) && (
                          <TableCell align="right">
                            {hasEditPermission && (
                              <Button
                                size="small"
                                onClick={() => navigate(`${Route}/edit/${i.id}`)}
                              >
                                Edit
                              </Button>
                            )}
                            {hasDeletePermission && (
                              <Button
                                size="small"
                                color="error"
                                onClick={() => setDeleteDialog(i.id)}
                              >
                                Delete
                              </Button>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ) : (
            <Box component="ul" sx={{ p: 0, listStyle: "none" }}>
              {current.map((i) => (
                <Box
                  component="li"
                  key={i.id}
                  sx={{
                    borderBottom: "1px solid #eee",
                    py: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1">{i.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {i.description}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1}>
                    {hasEditPermission && (
                      <Button
                        size="small"
                        onClick={() => navigate(`${Route}/edit/${i.id}`)}
                      >
                        Edit
                      </Button>
                    )}
                    {hasDeletePermission && (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => setDeleteDialog(i.id)}
                      >
                        Delete
                      </Button>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {/* Shared Pagination */}
          {filteredData?.length > perPage && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={2}
              mt={3}
            >
              <Button disabled={page === 0} onClick={() => changePage(-1)}>
                Previous
              </Button>
              <Typography>
                Page {page + 1} of {totalPages}
              </Typography>
              <Button
                disabled={page + 1 === totalPages}
                onClick={() => changePage(1)}
              >
                Next
              </Button>
            </Box>
          )}

          <Dialog
            open={!!deleteDialog}
            onClose={() => setDeleteDialog(null)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: "12px",
                overflow: "hidden",
                boxShadow: "0 8px 32px rgba(47, 54, 69, 0.14)",
              },
            }}
          >
            <DialogTitle
              sx={{
                m: 0,
                px: 2.5,
                py: 2,
                backgroundColor: "#fff",
                color: "#0891B2",
                borderBottom: "1px solid #e8eaef",
                fontWeight: 600,
                fontSize: "1.05rem",
              }}
            >
              {deleteConfirmTitle || "Confirm Delete"}
            </DialogTitle>
            <DialogContent sx={{ p: 3, backgroundColor: "#fff" }}>
              {deleteConfirmMessage ? (
                <>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {deleteConfirmMessage}
                  </Alert>
                  <Typography sx={{ color: "#4b5563", lineHeight: 1.6 }}>
                    Are you sure you want to permanently delete this{" "}
                    {messages?.[3]}? This action cannot be undone. This will also
                    delete all the related data.
                  </Typography>
                </>
              ) : (
                <Typography sx={{ color: "#4b5563", lineHeight: 1.6 }}>
                  Are you sure you want to permanently delete this{" "}
                  {messages?.[3]}? This action cannot be undone. This will also
                  delete all the related data.
                </Typography>
              )}
            </DialogContent>
            <DialogActions
              sx={{
                p: 3,
                pt: 0,
                backgroundColor: "#fff",
                justifyContent: "flex-end",
                gap: 1.5,
              }}
            >
              <Button
                variant="outlined"
                onClick={() => setDeleteDialog(null)}
                sx={{
                  color: "#6b7280",
                  borderColor: "#d1d5db",
                  minWidth: 100,
                  fontWeight: 600,
                  fontSize: "0.8125rem",
                  borderRadius: "9px",
                  textTransform: "none",
                  "&:hover": {
                    borderColor: "#9ca3af",
                    backgroundColor: "#f9fafb",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={confirmDelete}
                sx={{
                  backgroundColor: "#0891B2",
                  color: "#fff",
                  minWidth: 100,
                  fontWeight: 600,
                  fontSize: "0.8125rem",
                  borderRadius: "9px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#077d9c",
                    boxShadow: "none",
                  },
                }}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default Layout1;
